/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0 authorization code flow
 * Used for Microsoft Entra SSO authentication
 */

/**
 * Generates a cryptographically random code verifier for PKCE
 * @returns A random 43-128 character string using unreserved URI characters
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generates a code challenge from a code verifier using SHA-256
 * @param verifier The code verifier to hash
 * @returns The base64url-encoded SHA-256 hash of the verifier
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Generates a random state parameter to prevent CSRF attacks
 * @returns A random 16-character string
 */
export function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generates a random nonce for OIDC ID token binding.
 * The nonce ties the ID token to this specific authentication request,
 * preventing token replay attacks.
 * @returns A random base64url-encoded string
 */
export function generateNonce(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Base64url encodes a Uint8Array
 * This is different from standard base64: uses - and _ instead of + and /, no padding
 */
function base64UrlEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Storage keys for PKCE flow data
 */
export const PKCE_STORAGE_KEYS = {
  CODE_VERIFIER: 'entra_code_verifier',
  STATE: 'entra_state',
  EMAIL: 'entra_email',
  NONCE: 'entra_nonce',
};

/**
 * Stores PKCE flow data in sessionStorage
 */
export function storePKCEData(codeVerifier: string, state: string, email: string, nonce: string): void {
  sessionStorage.setItem(PKCE_STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
  sessionStorage.setItem(PKCE_STORAGE_KEYS.STATE, state);
  sessionStorage.setItem(PKCE_STORAGE_KEYS.EMAIL, email);
  sessionStorage.setItem(PKCE_STORAGE_KEYS.NONCE, nonce);
}

/**
 * Retrieves and clears PKCE flow data from sessionStorage
 * @returns The stored PKCE data or null if not found
 */
export function retrieveAndClearPKCEData(): { codeVerifier: string; state: string; email: string; nonce: string } | null {
  const codeVerifier = sessionStorage.getItem(PKCE_STORAGE_KEYS.CODE_VERIFIER);
  const state = sessionStorage.getItem(PKCE_STORAGE_KEYS.STATE);
  const email = sessionStorage.getItem(PKCE_STORAGE_KEYS.EMAIL);
  const nonce = sessionStorage.getItem(PKCE_STORAGE_KEYS.NONCE);

  if (!codeVerifier || !state || !email || !nonce) {
    return null;
  }

  // Clear the data after retrieval
  sessionStorage.removeItem(PKCE_STORAGE_KEYS.CODE_VERIFIER);
  sessionStorage.removeItem(PKCE_STORAGE_KEYS.STATE);
  sessionStorage.removeItem(PKCE_STORAGE_KEYS.EMAIL);
  sessionStorage.removeItem(PKCE_STORAGE_KEYS.NONCE);

  return { codeVerifier, state, email, nonce };
}

/**
 * Builds the Microsoft Entra authorization URL
 */
/**
 * Builds the Microsoft Entra authorization URL
 * @param forceLogin When true, adds prompt=login to force re-authentication
 *                   (used after explicit logout so the user must enter credentials again)
 */
export function buildEntraAuthUrl(
  tenantId: string,
  clientId: string,
  redirectUri: string,
  codeChallenge: string,
  state: string,
  email: string,
  nonce: string,
  forceLogin: boolean = false
): string {
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    // SSO scopes only - mailbox permissions are requested separately during mailbox connection
    scope: 'openid email profile offline_access',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state: state,
    nonce: nonce,
    login_hint: email,
    response_mode: 'query',
  });

  if (forceLogin) {
    params.set('prompt', 'login');
  }

  return `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params.toString()}`;
}
